import React, { useState } from 'react';
import { Mail, Phone, MapPin, Github, Linkedin, Send, CheckCircle } from 'lucide-react';
import { personalInfo } from '../data/mock';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSubmitted(true);
      setFormData({ name: '', email: '', subject: '', message: '' });
      
      // Reset success message after 3 seconds
      setTimeout(() => {
        setIsSubmitted(false);
      }, 3000);
    }, 1000);
  };

  return (
    <section id="contact" className="py-20 bg-gradient-to-br from-[rgb(17,17,19)] to-[rgb(26,28,30)] relative overflow-hidden">
      {/* Glassmorphism background elements */}
      <div className="absolute top-20 left-20 w-40 h-40 rounded-full bg-gradient-to-br from-[rgba(218,255,1,0.1)] to-transparent backdrop-blur-sm opacity-40"></div>
      <div className="absolute bottom-20 right-20 w-32 h-32 rounded-full bg-gradient-to-tl from-[rgba(218,255,1,0.08)] to-transparent backdrop-blur-sm opacity-30"></div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-6xl mx-auto">
          {/* Section Header with glass effect */}
          <div className="text-center mb-16">
            <div className="inline-block glass-light px-6 py-2 rounded-full mb-6">
              <span className="text-[rgb(218,255,1)] text-sm font-medium tracking-wide">LET'S CONNECT</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 relative">
              <span className="relative z-10">
                Get In <span className="text-[rgb(218,255,1)] relative">
                  Touch
                  <div className="absolute inset-0 bg-[rgb(218,255,1)] opacity-20 blur-xl rounded-lg"></div>
                </span>
              </span>
            </h2>
            <p className="text-xl text-[rgb(161,161,170)] max-w-3xl mx-auto leading-relaxed">
              Let's discuss AI/ML opportunities, collaborations, or just connect about technology
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Information with glassmorphism */}
            <div className="space-y-8">
              <div className="glass-card rounded-2xl p-8">
                <h3 className="text-2xl font-semibold text-white mb-6">
                  Let's <span className="text-[rgb(218,255,1)]">Connect</span>
                </h3>
                <p className="text-[rgb(161,161,170)] leading-relaxed mb-8">
                  I'm always interested in discussing AI/ML projects, potential collaborations, 
                  or opportunities in machine learning engineering and research. Feel free to reach out!
                </p>
              </div>

              {/* Contact Methods with enhanced glass effect */}
              <div className="space-y-4">
                {[
                  { icon: Mail, label: 'Email', value: personalInfo.email, href: `mailto:${personalInfo.email}` },
                  { icon: MapPin, label: 'Location', value: personalInfo.location }
                ].map((contact, index) => (
                  <div key={index} className="group glass-card rounded-2xl p-4 hover:glass-card transition-all duration-300 hover:transform hover:translate-y-[-2px]">
                    <div className="flex items-center space-x-4">
                      <div className="glass-light w-12 h-12 rounded-2xl flex items-center justify-center text-[rgb(218,255,1)] group-hover:shadow-[0_0_20px_rgba(218,255,1,0.3)] transition-all duration-300">
                        <contact.icon size={20} />
                      </div>
                      <div>
                        <div className="font-semibold text-white">{contact.label}</div>
                        {contact.href ? (
                          <a 
                            href={contact.href}
                            className="text-[rgb(161,161,170)] hover:text-[rgb(218,255,1)] transition-colors duration-300"
                          >
                            {contact.value}
                          </a>
                        ) : (
                          <div className="text-[rgb(161,161,170)]">{contact.value}</div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Social Links with glass effect */}
              <div className="glass-card rounded-2xl p-6">
                <h4 className="font-semibold text-white mb-4">Connect on Social</h4>
                <div className="flex space-x-4">
                  <a
                    href={personalInfo.github}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="glass-light w-12 h-12 rounded-2xl flex items-center justify-center text-[rgb(161,161,170)] hover:text-[rgb(218,255,1)] hover:border-[rgba(218,255,1,0.3)] hover:transform hover:translate-y-[-2px] hover:shadow-[0_0_20px_rgba(218,255,1,0.3)] transition-all duration-300"
                  >
                    <Github size={20} />
                  </a>
                  <a
                    href={personalInfo.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="glass-light w-12 h-12 rounded-2xl flex items-center justify-center text-[rgb(161,161,170)] hover:text-[rgb(218,255,1)] hover:border-[rgba(218,255,1,0.3)] hover:transform hover:translate-y-[-2px] hover:shadow-[0_0_20px_rgba(218,255,1,0.3)] transition-all duration-300"
                  >
                    <Linkedin size={20} />
                  </a>
                </div>
              </div>
            </div>

            {/* Contact Form with glassmorphism */}
            <div className="glass-card rounded-2xl p-8">
              {isSubmitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 mx-auto mb-4 glass-light rounded-full flex items-center justify-center">
                    <CheckCircle size={32} className="text-[rgb(218,255,1)]" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">Message Sent!</h3>
                  <p className="text-[rgb(161,161,170)]">Thank you for reaching out. I'll get back to you soon!</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-6 flex items-center gap-3">
                      <div className="w-2 h-8 bg-[rgb(218,255,1)] rounded-full shadow-[0_0_10px_rgba(218,255,1,0.5)]"></div>
                      Send a Message
                    </h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-white mb-2">
                        Name
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full glass-input rounded-xl px-4 py-3 text-white placeholder-[rgb(161,161,170)] focus:outline-none transition-all duration-300"
                        placeholder="Your name"
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-white mb-2">
                        Email
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full glass-input rounded-xl px-4 py-3 text-white placeholder-[rgb(161,161,170)] focus:outline-none transition-all duration-300"
                        placeholder="your.email@example.com"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-white mb-2">
                      Subject
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="w-full glass-input rounded-xl px-4 py-3 text-white placeholder-[rgb(161,161,170)] focus:outline-none transition-all duration-300"
                      placeholder="What's this about?"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-white mb-2">
                      Message
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      className="w-full glass-input rounded-xl px-4 py-3 text-white placeholder-[rgb(161,161,170)] focus:outline-none transition-all duration-300 resize-none"
                      placeholder="Tell me more about your project or opportunity..."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`w-full flex items-center justify-center space-x-2 py-4 px-6 rounded-xl font-semibold text-lg transition-all duration-300 relative overflow-hidden ${
                      isSubmitting
                        ? 'glass-light text-[rgb(161,161,170)] cursor-not-allowed'
                        : 'glass-button text-[rgb(17,17,19)] hover:transform hover:translate-y-[-2px] hover:shadow-[0_8px_25px_rgba(218,255,1,0.3)]'
                    }`}
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-[rgb(161,161,170)] border-t-transparent rounded-full animate-spin"></div>
                        <span>Sending...</span>
                      </>
                    ) : (
                      <>
                        <Send size={20} className="relative z-10" />
                        <span className="relative z-10">Send Message</span>
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></div>
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;